import { type PropsWithChildren, useEffect } from 'react';
import { captureException } from '@sentry/react';
import { CrashPage } from 'app/components/CrashPage/CrashPage';
import { LoadingSpinner } from 'app/components/Loading/Loading';
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useRouteError } from 'react-router';

export const Layout = ({ children }: PropsWithChildren) => (
  <html lang="en">
    <head suppressHydrationWarning>
      <HeadMeta />
      <link href="/fonts/inter/inter.css" rel="stylesheet" />
      <Links />
    </head>
    <body>
      <noscript>You need to enable JavaScript to run this app.</noscript>
      <script
        dangerouslySetInnerHTML={{
          __html: `
          window.global = window;
          (function(){
            var isIExplorer = !!document.documentMode;
            if (isIExplorer) {
              window.location.assign("not-supported.html")
            }
          })();`,
        }}
      />
      <div id="root">{children}</div>
      <ScrollRestoration />
      <Scripts />
    </body>
  </html>
);

export const HydrateFallback = () => <LoadingSpinner variant="screen" />;

export const ErrorBoundary = () => {
  const error = useRouteError();
  useEffect(() => {
    // TODO: do sentry stuff
    if (error) captureException(error);
  }, [error]);
  return <CrashPage />;
};

const Root = () => <Outlet />;
export default Root;

const HeadMeta = () => (
  <>
    <meta charSet="utf-8" />
    <meta
      name="viewport"
      content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, maximum-scale=1, user-scalable=0"
    />
    <ContentSecurityPolicy />
    <meta property="og:url" content="https://app.shape.construction" />
    <meta property="og:type" content="website" />
    <meta property="og:title" content="Shape" />
    {/* Absolute URL to avoid crawlers that fetch the url directly */}
    <meta property="og:image" content="https://app.shape.construction/images/splashscreens/og-share-image.png" />
    <meta property="og:site_name" content="Shape" />
    <meta
      property="og:description"
      content="Shape is a place where construction teams can create, track and resolve issues seamlessly on mobile and desktop. Teams using Shape collaborate more effectively and close-out defects and safety issues faster and smarter!"
    />
    <Meta />

    <link rel="shortcut icon" href="/favicon.ico" />
    {/* React Router generates index.html after the build, so Vite's transformIndexHtml never runs
        and vite-plugin-pwa cannot inject this — without it Chrome never discovers the manifest.
        Dev links the static public/ copy, because the plugin only serves its generated manifest
        when devOptions is on. `start:pwa` deliberately takes this branch too, so the env-prefixed
        name from getAppPrefixName is only ever exercised in a build. */}
    <link rel="manifest" href={import.meta.env.DEV ? '/manifest.json' : '/manifest.webmanifest'} />

    {/* Apple icons */}
    <link rel="apple-touch-icon" sizes="144x144" href="/images/icons/main/maskable_icon_x144.png" />
    <link rel="apple-touch-icon" sizes="152x152" href="/images/icons/main/maskable_icon_x152.png" />
    <link rel="apple-touch-icon" sizes="167x167" href="/images/icons/main/maskable_icon_x167.png" />
    <link rel="apple-touch-icon" sizes="180x180" href="/images/icons/main/maskable_icon_x180.png" />
    <link rel="apple-touch-icon" sizes="384x384" href="/images/icons/main/maskable_icon_x384.png" />
    <link rel="apple-touch-icon" sizes="512x512" href="/images/icons/main/maskable_icon_x512.png" />
    <link rel="apple-touch-icon" href="/images/icons/maskable_icon_x512.png" />

    {/* Apple splash screens */}
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
      href="/images/splashscreens/iPad-12.9_-Pro(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
      href="/images/splashscreens/iPad-12.9_-Pro(horizontal)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
      href="/images/splashscreens/iPad-11_-Pro(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
      href="/images/splashscreens/iPad-11_-Pro(horizontal)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 820px) and (device-height: 1180px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
      href="/images/splashscreens/iPad-10.9_-4thGen(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 820px) and (device-height: 1180px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
      href="/images/splashscreens/iPad-10.9_-4thGen(horizontal)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
      href="/images/splashscreens/iPad-10.5_-Air(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
      href="/images/splashscreens/iPad-10.5_-Air(horizontal)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
      href="/images/splashscreens/iPad-10.2_(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
      href="/images/splashscreens/iPad-10.2_(horizontal)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
      href="/images/splashscreens/iPad-9.7_-Pro(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: landscape)"
      href="/images/splashscreens/iPad-9.7_-Pro(horizontal)_shape_splashscreen.png"
    />

    <link
      rel="apple-touch-startup-image"
      media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
      href="/images/splashscreens/iPhone-14__iPhone-13-Pro__iPhone-13__iPhone-12-Pro__iPhone-12(landscape)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
      href="/images/splashscreens/iPhone-14__iPhone-13-Pro__iPhone-13__iPhone-12-Pro__iPhone-12(portrait)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
      href="/images/splashscreens/iPhone-14-Plus__iPhone-13-ProMax__iPhone-12-ProMax(landscape)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
      href="/images/splashscreens/iPhone-14-Plus__iPhone-13-ProMax__iPhone-12-ProMax(portrait)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
      href="/images/splashscreens/iPhone-14-ProMax(landscape)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
      href="/images/splashscreens/iPhone-14-ProMax(portrait)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: landscape)"
      href="/images/splashscreens/iPhone-14-Pro(landscape)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
      href="/images/splashscreens/iPhone-14-Pro(portrait)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
      href="/images/splashscreens/iPhone-12-mini(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
      href="/images/splashscreens/iPhone-11(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
      href="/images/splashscreens/iPhone-11-Pro-Max(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
      href="/images/splashscreens/iPhone-8-Plus(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
      href="/images/splashscreens/iPhone-8(vertical)_shape_splashscreen.png"
    />
    <link
      rel="apple-touch-startup-image"
      media="(device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
      href="/images/splashscreens/iPhone-SE(vertical)_shape_splashscreen.png"
    />
  </>
);

// Load and render csp config when building index.html
const contentSecurityPolicy = import.meta.env.SSR
  ? await import('../config/csp').then((csp) => csp.getContentSecurityPolicy())
  : undefined;

export const ContentSecurityPolicy = () => {
  if (!contentSecurityPolicy) return null;
  return <meta httpEquiv="Content-Security-Policy" content={contentSecurityPolicy} />;
};
